import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const EditInvoiceModal = ({ isOpen, onClose, onUpdate, invoice }) => {
  const [formData, setFormData] = useState({
    platform_charge: 0,
    adjustments: 0,
    discount: 0,
    gst_percentage: 18,
    description: '',
    status: 'draft'
  });

  useEffect(() => {
    if (invoice) {
      setFormData({
        platform_charge: invoice.platform_charge || 0,
        adjustments: invoice.adjustments || 0,
        discount: invoice.discount || 0,
        gst_percentage: invoice.gst_percentage || 18,
        description: invoice.description || '',
        status: invoice.status || 'draft'
      });
    }
  }, [invoice]);

  if (!isOpen || !invoice) return null;

  const subtotal = invoice.subtotal;
  const platformCharge = parseFloat(formData.platform_charge || 0);
  const adjustments = parseFloat(formData.adjustments || 0);
  const discount = parseFloat(formData.discount || 0);
  const gstPercentage = parseFloat(formData.gst_percentage || 0);
  
  const amountAfterAdjustments = subtotal + platformCharge + adjustments - discount;
  const gstAmount = (amountAfterAdjustments * gstPercentage) / 100;
  const finalAmount = amountAfterAdjustments + gstAmount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onUpdate(invoice.id, formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-3xl w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Invoice</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <div className="mb-2">
                  <p className="text-xs text-gray-600 dark:text-gray-400">Parent</p>
                  <p className="font-bold text-gray-900 dark:text-white">{invoice.parent_name}</p>
                </div>
                <div className="mb-2">
                  <p className="text-xs text-gray-600 dark:text-gray-400">Period</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {new Date(invoice.period_start).toLocaleDateString()} - {new Date(invoice.period_end).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Subtotal</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">₹{subtotal.toLocaleString()}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Platform Charge (₹)</label>
                <Input type="number" step="0.01" value={formData.platform_charge} onChange={(e) => setFormData({...formData, platform_charge: e.target.value})} placeholder="0.00" />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Adjustments (₹)</label>
                <Input type="number" step="0.01" value={formData.adjustments} onChange={(e) => setFormData({...formData, adjustments: e.target.value})} placeholder="0.00" />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Add bonus or additional charges</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Discount (₹)</label>
                <Input type="number" step="0.01" value={formData.discount} onChange={(e) => setFormData({...formData, discount: e.target.value})} placeholder="0.00" />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Apply discount amount</p>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">GST (%)</label>
                <Input type="number" step="0.01" value={formData.gst_percentage} onChange={(e) => setFormData({...formData, gst_percentage: e.target.value})} placeholder="18" />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Status</label>
                <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <option value="draft">Draft</option>
                  <option value="submitted">Submitted</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none" rows="6" placeholder="Add notes or description" />
              </div>
            </div>
          </div>

          {/* Calculation Summary */}
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
            <div className="grid grid-cols-2 gap-4 text-sm mb-2">
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                  <span className="text-gray-900 dark:text-white">₹{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Platform Charge:</span>
                  <span className="text-green-600">+₹{platformCharge.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Adjustments:</span>
                  <span className={adjustments >= 0 ? 'text-green-600' : 'text-red-600'}>{adjustments >= 0 ? '+' : ''}₹{adjustments.toFixed(2)}</span>
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Discount:</span>
                  <span className="text-red-600">-₹{discount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">GST ({gstPercentage}%):</span>
                  <span className="text-gray-900 dark:text-white">₹{gstAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
            <div className="border-t border-green-200 dark:border-green-800 pt-2 mt-2">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-green-700 dark:text-green-400">Final Amount</span>
                <span className="text-2xl font-bold text-green-600 dark:text-green-400">₹{finalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button type="button" onClick={onClose} variant="outline" className="flex-1">Cancel</Button>
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">Update Invoice</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditInvoiceModal;
